import React from 'react';
import { cn } from '../../utils/cn';

export interface LogoProps {
  /** Height of the mark in pixels. Width scales to preserve the 44:33 aspect ratio. */
  size?: number;
  /** Renders the lowercase "dfx5" wordmark beside the mark. */
  withWordmark?: boolean;
  className?: string;
}

/**
 * Inlined DFX5 brand mark (source: src/assets/dfx5-mark.svg).
 * Paths are inlined as JSX rather than loaded via <img>/svgr so the mark
 * renders reliably with no build-time SVG tooling or network dependency.
 */
const Logo: React.FC<LogoProps> = ({ size = 33, withWordmark = false, className }) => {
  const width = (size * 44) / 33;

  return (
    <span className={cn('inline-flex items-center gap-2', className)}>
      <svg
        width={width}
        height={size}
        viewBox="0 0 44 33"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        role="img"
        aria-label="DFX5"
      >
        <g clipPath="url(#dfx5-mark-clip)">
          <mask
            id="dfx5-mark-mask"
            style={{ maskType: 'alpha' }}
            maskUnits="userSpaceOnUse"
            x="0"
            y="0"
            width="44"
            height="33"
          >
            <path
              d="M41.9397 9.31075C39.8152 11.4352 36.3702 11.4352 34.2457 9.31075C32.1212 7.18627 32.1212 3.74129 34.2457 1.6168C36.3702 -0.507683 39.8152 -0.507683 41.9397 1.6168C44.0618 3.74129 44.0618 7.18627 41.9397 9.31075Z"
              fill="black"
            />
            <path
              d="M32.6526 16.3251C32.6526 18.0172 31.8813 19.527 30.673 20.525C30.5211 20.6489 30.3645 20.7657 30.1985 20.8756C30.1448 20.913 30.0887 20.9457 30.0326 20.9807C29.9578 21.0275 29.883 21.0696 29.8059 21.1116C29.6516 21.1958 29.4927 21.2706 29.3291 21.3407C28.6794 21.6165 27.9642 21.7684 27.2116 21.7684C24.2084 21.7684 21.7707 24.2014 21.7684 27.2046C21.7684 27.207 21.7684 27.207 21.7684 27.2093C21.7684 30.2172 19.333 32.6526 16.3251 32.6526C16.2223 32.6526 16.1194 32.6502 16.0166 32.6432C15.9979 32.6432 15.9815 32.6409 15.9628 32.6385C15.8881 32.6362 15.8133 32.6292 15.7385 32.6198C15.6894 32.6152 15.638 32.6105 15.5889 32.6011C15.5422 32.5965 15.4954 32.5894 15.4487 32.5801C15.4136 32.5754 15.3786 32.5708 15.3435 32.5614C15.2664 32.5497 15.1892 32.5334 15.1145 32.517C15.1004 32.5147 15.0887 32.5123 15.0794 32.5076C14.9929 32.4889 14.9041 32.4656 14.82 32.4399C14.8083 32.4375 14.7989 32.4352 14.7896 32.4305C14.7148 32.4095 14.64 32.3861 14.5652 32.3604C14.5442 32.3534 14.5232 32.3464 14.5021 32.3394C14.4133 32.3066 14.3245 32.2739 14.238 32.2365C14.1469 32.1991 14.0557 32.1571 13.9669 32.115C13.8547 32.0612 13.7449 32.0052 13.6397 31.9421C13.5836 31.9117 13.5252 31.8766 13.4691 31.8416C13.3943 31.7971 13.3218 31.7504 13.2517 31.699C13.2073 31.6709 13.1629 31.6382 13.1209 31.6055C13.0928 31.5891 13.0648 31.5681 13.0391 31.5447C12.9946 31.512 12.9502 31.4793 12.9058 31.4396C12.8427 31.3928 12.7796 31.3391 12.7212 31.283C12.6745 31.2456 12.63 31.2035 12.588 31.1638C12.5553 31.1357 12.5272 31.1053 12.4968 31.0773C12.3659 30.9488 12.2444 30.8132 12.1276 30.673C12.0715 30.6052 12.0177 30.5374 11.9663 30.4673C11.9149 30.3995 11.8658 30.3317 11.8191 30.2616C11.7186 30.1121 11.6274 29.9601 11.5409 29.8059C11.4568 29.6516 11.382 29.4927 11.3119 29.3291C11.0361 28.677 10.8842 27.9619 10.8842 27.2116V27.2093C10.8818 24.2014 8.44652 21.7684 5.44092 21.7684C2.43533 21.7684 0 19.3307 0 16.3251C0 13.3195 2.43533 10.8842 5.44092 10.8842C8.44886 10.8842 10.8842 13.3195 10.8842 16.3251C10.8842 19.3307 13.3195 21.7684 16.3274 21.7684C19.333 21.7684 21.766 19.333 21.7684 16.3274V16.3251C21.766 13.3172 19.333 10.8842 16.3274 10.8842C13.3218 10.8842 10.8842 8.44652 10.8842 5.44092C10.8842 2.43533 13.3195 0 16.3274 0C19.333 0 21.7684 2.43533 21.7684 5.44092C21.7684 8.44418 24.2037 10.8818 27.207 10.8842C27.2093 10.8842 27.2093 10.8842 27.2116 10.8842C27.9619 10.8842 28.6794 11.0361 29.3291 11.3119C29.4647 11.368 29.5955 11.4311 29.7241 11.5012C29.9087 11.5923 30.0863 11.6999 30.2546 11.8144C30.3271 11.8635 30.3972 11.9125 30.4673 11.9663C30.5374 12.0177 30.6052 12.0715 30.673 12.1276C30.673 12.1276 30.673 12.1276 30.6753 12.1276C30.7407 12.1836 30.8062 12.2397 30.8693 12.2982C30.9347 12.3566 30.9978 12.4174 31.0586 12.4781C31.498 12.9152 31.8603 13.4247 32.1267 13.9879C32.1991 14.1375 32.2646 14.2941 32.323 14.4507C32.5357 15.035 32.6526 15.666 32.6526 16.3251Z"
              fill="black"
            />
          </mask>
          <g mask="url(#dfx5-mark-mask)">
            <path d="M27.6555 8.50218L20.0859 16.6875L28.8936 24.8326L36.4632 16.6473L27.6555 8.50218Z" fill="#806EE1" />
            <path d="M3.78833 7.29906L-3.78125 15.4844L5.02643 23.6295L12.596 15.4442L3.78833 7.29906Z" fill="#E0D26E" />
            <path d="M16.0657 19.3147L8.49609 27.5L17.3038 35.6451L24.8734 27.4598L16.0657 19.3147Z" fill="#6EE0AB" />
            <g filter="url(#dfx5-mark-filter-0)">
              <path d="M4.27344 23.5974L18.2894 8.44141L31.3214 20.4931L17.3055 35.6492L4.27344 23.5974Z" fill="#6EE0AB" />
            </g>
            <g filter="url(#dfx5-mark-filter-1)">
              <path d="M13.1523 13.0001L23.9282 1.34766L39.5892 15.8306L28.8133 27.4831L13.1523 13.0001Z" fill="#806EE1" />
            </g>
            <g filter="url(#dfx5-mark-filter-2)">
              <path d="M3.08984 5.70468L15.5108 -7.72656L30.0779 5.7448L17.657 19.176L3.08984 5.70468Z" fill="#E07D6E" />
            </g>
            <path d="M37.58 -3.01197L29.4844 5.74219L38.2921 13.8873L46.3877 5.13316L37.58 -3.01197Z" fill="#806EE1" />
            <path d="M15.2961 -3.08766L7.72656 5.09766L16.5342 13.2428L24.1038 5.05747L15.2961 -3.08766Z" fill="#E07D6E" />
            <g filter="url(#dfx5-mark-filter-3)">
              <path d="M-8.76562 16.148L2.75389 3.69141L17.6389 17.4568L6.1194 29.9133L-8.76562 16.148Z" fill="#E0D26E" />
            </g>
          </g>
        </g>
        <defs>
          <filter id="dfx5-mark-filter-0" x="-5.2399" y="-1.07193" width="46.0736" height="46.2347" filterUnits="userSpaceOnUse" colorInterpolationFilters="sRGB">
            <feFlood floodOpacity="0" result="BackgroundImageFix" />
            <feBlend mode="normal" in="SourceGraphic" in2="BackgroundImageFix" result="shape" />
            <feGaussianBlur stdDeviation="4.75667" result="effect1_foregroundBlur" />
          </filter>
          <filter id="dfx5-mark-filter-1" x="3.639" y="-8.16568" width="45.4642" height="45.1619" filterUnits="userSpaceOnUse" colorInterpolationFilters="sRGB">
            <feFlood floodOpacity="0" result="BackgroundImageFix" />
            <feBlend mode="normal" in="SourceGraphic" in2="BackgroundImageFix" result="shape" />
            <feGaussianBlur stdDeviation="4.75667" result="effect1_foregroundBlur" />
          </filter>
          <filter id="dfx5-mark-filter-2" x="-6.4235" y="-17.2399" width="46.015" height="45.9295" filterUnits="userSpaceOnUse" colorInterpolationFilters="sRGB">
            <feFlood floodOpacity="0" result="BackgroundImageFix" />
            <feBlend mode="normal" in="SourceGraphic" in2="BackgroundImageFix" result="shape" />
            <feGaussianBlur stdDeviation="4.75667" result="effect1_foregroundBlur" />
          </filter>
          <filter id="dfx5-mark-filter-3" x="-18.279" y="-5.82193" width="45.4329" height="45.2484" filterUnits="userSpaceOnUse" colorInterpolationFilters="sRGB">
            <feFlood floodOpacity="0" result="BackgroundImageFix" />
            <feBlend mode="normal" in="SourceGraphic" in2="BackgroundImageFix" result="shape" />
            <feGaussianBlur stdDeviation="4.75667" result="effect1_foregroundBlur" />
          </filter>
          <clipPath id="dfx5-mark-clip">
            <rect width="44" height="33" fill="white" />
          </clipPath>
        </defs>
      </svg>
      {withWordmark && (
        <span className="font-sans font-bold text-[var(--foreground)] lowercase tracking-tight" style={{ fontSize: size * 0.7 }}>
          dfx5
        </span>
      )}
    </span>
  );
};

export default Logo;
